import axios from "axios";

const SESSION_KEYS = [
    "authToken",
    "authTokenExpiresAt",
    "user",
    "permissions",
    "role",
    "AuthUser",
];

export const saveSession = (data) => {
    localStorage.setItem("authToken", data?.token || "");
    localStorage.setItem("user", JSON.stringify(data?.user || {}));
    localStorage.setItem("permissions", JSON.stringify(data?.permissions || []));
    localStorage.setItem("role", data?.roles || "");

    const now = new Date();
    const expiresAt = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() + 1,
        0, 0, 0, 0
    ).getTime();

    localStorage.setItem("authTokenExpiresAt", String(expiresAt));
};

export const clearSession = () => {
    SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
};

export const getSessionExpiry = () => {
    const raw = localStorage.getItem("authTokenExpiresAt");
    const value = raw ? parseInt(raw, 10) : NaN;

    return Number.isNaN(value) ? null : value;
};

export const isSessionExpired = () => {
    if (!localStorage.getItem("authToken")) return false;

    const expiry = getSessionExpiry();

    return expiry !== null && expiry <= Date.now();
};

export const endSession = (reason) => {
    clearSession();

    if (window.location.pathname === "/login") return;

    window.location.href = `/login?reason=${reason || "expired"}`;
};

// Only our own backend is given the token; anything else a page fetches is
// left alone.
const isApiCall = (url) => {
    if (!url) return false;

    const bases = [process.env.REACT_APP_API_URL, process.env.REACT_APP_URL].filter(Boolean);

    return bases.some((base) => url.startsWith(base));
};

const hasAuthHeader = (config) => {
    const headers = config?.headers || {};

    return Boolean(
        headers.Authorization ||
        headers.authorization ||
        headers.common?.Authorization ||
        (typeof headers.get === "function" && headers.get("Authorization"))
    );
};

export const installSessionGuard = () => {
    if (isSessionExpired()) {
        endSession("expired");
        return;
    }

    // A page that forgets the header gets a 401 that has nothing to do with the
    // session, and the response guard below would sign the user out for it. The
    // token is attached here so every call to our backend carries it.
    axios.interceptors.request.use((config) => {
        const token = localStorage.getItem("authToken");

        if (token && isApiCall(config?.url) && !hasAuthHeader(config)) {
            config.headers = config.headers || {};

            // axios hands interceptors an AxiosHeaders object, but a plain
            // object turns up in tests and older callers.
            if (typeof config.headers.set === "function") {
                config.headers.set("Authorization", `Bearer ${token}`);
            } else {
                config.headers.Authorization = `Bearer ${token}`;
            }
        }

        return config;
    });

    axios.interceptors.response.use(
        (response) => response,
        (error) => {
            const status = error?.response?.status;
            const code = error?.response?.data?.error_code;
            const isLoginCall = (error?.config?.url || "").includes("/login");

            // A 401 only means the session is over when the request actually
            // presented the token; without one it proves nothing.
            const sentToken = hasAuthHeader(error?.config);

            if (!isLoginCall && localStorage.getItem("authToken")) {
                if (code === "ACCOUNT_INACTIVE") {
                    endSession("inactive");
                } else if (status === 401 && sentToken) {
                    endSession("expired");
                }
            }

            return Promise.reject(error);
        }
    );

    window.setInterval(() => {
        if (isSessionExpired()) {
            endSession("expired");
        }
    }, 60000);
};
